//================================================================
//  Page: Help
//================================================================

//  Purpose: Help page for DERP

//  Supporting documentation
//    - N/A

//================================================================

//Libraries
import { useContext, useState } from 'react';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components
import PageComponent from '../../Components/PageComponent/PageComponent';
import Issue from './Forms/Issue';
import Improvement from './Forms/Improvement';

//Functions

//Images
import ArrowRight from '../../Components/Images/Icon_Angle_Right.svg';
import ArrowDown from '../../Components/Images/Icon_Angle_ArrowDown.svg';

//CSS
import './Help.css';

export default function Help() {

    //------------------------------------------------------
    //  useContexts & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Controller for which form is showing > Issue, Improvement
    const [form, setForm] = useState('');

    // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('onload');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    const getFormat = (formTitle) => {

        // Default state - no form is showing
        if (form === '') return 'bg-white flex flex-col gap-2 rounded-[6px] w-[40%] h-45 p-6 overflow-hidden shadow-md border-solid border-1 border-neutral-200 cursor-pointer';

        // Selected state
        else if (form === formTitle) return 'bg-white flex flex-col gap-2 rounded-[6px] w-[40%] h-45 p-6 overflow-hidden shadow-md border-solid border-1 border-[#0972D3] cursor-pointer';

        // Disabled state
        else return 'bg-neutral-100 flex flex-col gap-2 rounded-[6px] w-[40%] h-45 p-6 overflow-hidden shadow-md border-solid border-1 border-neutral-200 cursor-pointer';

    }

    //------------------------------------------------------
    // Function to get the portals based on user roles
    //------------------------------------------------------

    function getPortalsFromRoles(roles) {


        const roleToPortalMap = {
            businessPartnerUser: 'Demand Management',
            itUser: 'Inventory',
            commercialUser: 'Commercial',
            procurementUser: 'Procurement',
        };

        const portals = Object.entries(roleToPortalMap)
            .filter(([role]) => roles[role])
            .map(([, portal]) => portal);

        // Always place "Digital Services" first
        return ['Digital Services', ...portals];
    }

    //  Get portals based on user roles
    const portals = getPortalsFromRoles(getUser.roles);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            body={
                <div className='flex flex-col h-full p-3 md:p-4 gap-4 mt-6'>

                    {/* Greeting */}
                    <div >

                        <div className='flex flex-col justify-center items-center gap-1 mb-4'>
                            <h4 className='h-full cursor-default text-[24px]'>
                                Hello {getUser?.givenname},
                            </h4>
                            <p className='text-[#424242] font-sans text-[21px] leading-[24px] m-0'>how can we help you?
                            </p>
                        </div>

                        <div className='hidden md:flex flex-wrap gap-3 py-3 place-content-center'>

                            {/* Report an Issue */}
                            <div className={getFormat('Issue')} onClick={() => setForm('Issue')}>
                                <p className='font-semibold mb-0'>Report an Issue</p>
                                <p className='line-clamp-2 mb-0 text-[14px]'>With technical issues and bugs you encounter. Your feedback helps us improve your experience.</p>
                                <div className='flex flex-row gap-2 items-center justify-center rounded-full bg-[#E3F1FB] px-3 mt-1 py-2 w-[140px]'>
                                    <p className='font-semibold text-[14px] mb-0'>Get support</p>
                                    <img src={form === 'Issue' ? ArrowDown : ArrowRight} alt='Arrow' className='w-[21px]'></img>
                                </div>
                            </div>

                            {/* Suggest an Improvement */}
                            <div className={getFormat('Improvement')} onClick={() => setForm('Improvement')}>
                                <p className='font-semibold mb-0'>Suggest an Improvement</p>
                                <p className='line-clamp-2 mb-0 text-[14px]'>To our current processes or portals within our Digital ERP. We value your input in making things better!</p>
                                <div className='flex flex-row gap-2 items-center justify-center rounded-full bg-[#E3F1FB] px-3 mt-1 py-2 w-[140px]'>
                                    <p className='font-semibold text-[14px] mb-0'>Get started</p>
                                    <img src={form === 'Improvement' ? ArrowDown : ArrowRight} alt='Arrow' className='w-[21px]'></img>
                                </div>
                            </div>


                        </div>

                        {/* Mobile - Show Dropdown */}
                        <div className='md:hidden'>
                            <div className='bg-white rounded-[10px] p-3 w-full shadow-md border-solid border-1 border-neutral-300'>

                                <select className='Input-Field-Select w-full' onChange={(e) => setForm(e.target.value)}>
                                    <option hidden value=''>Select</option>
                                    <option value='Issue'>Report an Issue</option>
                                    <option value='Improvement'>Suggest an Improvement</option>
                                </select>
                            </div>
                        </div>

                    </div>

                    {/* =============================== */}
                    {/*          REQUEST FORM           */}
                    {/* =============================== */}

                    <div className='flex self-center w-full md:w-[81%] ' hidden={form === ''}>

                        {/* Gets selected form */}
                        {
                            form === 'Issue' ?
                                <Issue setPageStatus={setPageStatus} portals={portals}></Issue> :
                                <Improvement setPageStatus={setPageStatus} portals={portals }></Improvement>
                        }

                    </div>

                </div>
            }
            successContent={
                <div className='flex flex-col h-full p-3 mt-6 w-full items-center'>
                    <div className='flex flex-col md:w-[80%] gap-4'>
                        <div className='flex flex-col justify-center items-center gap-1 mb-4'>
                            <h4 className='h-full cursor-default text-[24px]'>
                                Hello {getUser?.givenname},
                            </h4>
                            <p className='text-[#424242] font-sans text-[21px] leading-[24px] m-0'>how can we help you?
                            </p>
                        </div>

                        <div className='bg-[#e7f2da] p-4 flex flex-col gap-2 rounded-md shadow-md border-l-8 border-l-[#8ABB45]'>
                            <label className='font-semibold text-lg'>{form === 'Issue' ? 'You have successfully reported an issue' : 'You have successfully suggested an improvement'}</label>
                            <p className='text-neutral-600 mb-0'>
                                {form === 'Issue' ? 'Thank you for reporting the bug. We have received your report, and our team will investigate the issue. We will reach out to you soon with an update. We appreciate your help in improving our product!' : 'Thank you for sharing your feedback. We have received it and a team member will be in touch with you shortly. Your input is greatly appreciated! '}
                            </p>
                        </div>

                        <div className='flex gap-2 flex-col md:flex-row'>

                            <button className='Primary-Button' onClick={() => {
                                setForm('');
                                setPageStatus('onload');
                            }}>
                                Go back
                            </button>

                        </div>
                    </div>
                </div>
            }
        ></PageComponent>
    );
}
